import { StorageKeys } from "@origin-digital/platform-enums";
import bannerConfig from "./configurations.json";
import {
  BannerId,
  BannerInfo,
  BannerContentType,
  CrisisBannerContent,
  SecurityBannerContent,
} from "./helpers";

async function doFetch(url, requestOptions): Promise<any> {
  const response = await fetch(url, requestOptions);
  if (!response.ok) {
    throw new Error(
      `${url} failed to fetch. Status code was ${response.status} `
    );
  }
  return response.headers.get("content-length") === "0"
    ? null
    : response.json();
}

async function fetchContent(url) {
  /**
   * setting cache to no-cache to force the browser to get content from the cms
   * server as cms content can change rapidly especially when authors edit and
   * publishes multiple times within a very short period.
   */
  return doFetch(url, {
    method: "GET",
    cache: "no-cache",
  });
}

function getContentApiEndpoint(identifier: BannerId) {
  const { env } = window.oetal || {};
  const { name: envName = "local" } = env || {};
  const config = bannerConfig[envName];
  switch (identifier) {
    case StorageKeys.crisisBanner:
      return config.cbContentApi;
    case StorageKeys.securitybanner:
      return config.sbContentApi;
    default:
  }
}

function loadContentFromLocalStorage(key: BannerId): BannerInfo | null {
  const localContentStr = localStorage.getItem(key);
  if (!localContentStr) {
    return null;
  }

  return JSON.parse(localContentStr);
}

async function loadContentFromCms(
  bannerId: BannerId,
  preview?: boolean
): Promise<BannerInfo> {
  const response = await fetchContent(getContentApiEndpoint(bannerId));
  if (!response || Object.keys(response).length === 0) {
    throw new Error("Banner content api response can't be blank");
  }

  const bannerContent = transformBannerContent(response, bannerId);
  return createBannerInfo(bannerId, bannerContent, preview);
}

function createBannerInfo(
  bannerId: BannerId,
  bannerContent: BannerContentType | undefined,
  preview?: boolean
): BannerInfo {
  let bannerInfo: BannerInfo;
  if (bannerContent && (bannerContent.enabled || preview)) {
    bannerInfo = {
      status: "open",
      ...(bannerId === StorageKeys.securitybanner
        ? { messageType: "msgDefault" }
        : {}),
      content: bannerContent,
    };
  } else {
    bannerInfo = {
      status: "disabled",
    };
  }

  return bannerInfo;
}

function transformSecurityBannerMsgKeys(content): SecurityBannerContent {
  return {
    msgDefault: content.breachMessage,
    msgError: content.errorMessage,
    msgA: content.breachMessageA,
    msgB: content.breachMessageB,
    msgNB: content.noBreachMessage,
    enabled: content.enabled,
  };
}

function transformCrisisBannerMsgKeys(content): CrisisBannerContent {
  return {
    start: content.crisisTime,
    end: content.crisisOverTime,
    type: content.crisisBannerType === "1" ? "warning" : "info",
    msg: content.crisisMessage,
    heading: content.crisisHeading,
    link: content.crisisLinkURL,
    linkText: content.crisisLinkText,
    enabled: content.crisisEnabled,
    location: content.crisisLocation ? content.crisisLocation : [],
  };
}

function renameCmsContentObjKeys(
  cmsContentObj,
  id: BannerId
): BannerContentType {
  let renamedObj;
  if (id === StorageKeys.securitybanner) {
    renamedObj = transformSecurityBannerMsgKeys(cmsContentObj);
  } else if (id === StorageKeys.crisisBanner) {
    renamedObj = transformCrisisBannerMsgKeys(cmsContentObj);
  }
  return renamedObj;
}

/**
 * Banner content is stored in ACF blocks in WordPress and the content
 * is stored under the `ACF` key
 * @param response
 * @param key
 */
function transformBannerContent(
  response,
  key: BannerId
): BannerContentType | undefined {
  const bannerContent = response[0]["ACF"];
  return bannerContent && Object.keys(bannerContent).length !== 0
    ? renameCmsContentObjKeys(bannerContent, key)
    : undefined;
}

export { doFetch, loadContentFromCms, loadContentFromLocalStorage };
